import { useEffect, useState } from 'react';

import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';

export interface PendingInvite {
  token: string;
  novelTitle: string;
  role: string;
  ownerId: string;
}

interface InvitationDetailsResponse {
  success: boolean;
  novelTitle: string;
  role: string;
  ownerId: string;
}

interface CheckPendingResponse {
  success: boolean;
  invitations: Array<{
    token: string;
    novelTitle: string;
    role: string;
    ownerId: string;
  }>;
}

export function useDashboardInvitations(inviteToken?: string) {
  const { backend, user } = useFirebase();
  const { userData } = useUserProfile();
  const [pendingInvite, setPendingInvite] = useState<PendingInvite | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const checkInvites = async () => {
      if (!user || !backend) return;

      if (inviteToken) {
        try {
          setIsProcessing(true);
          const res = await backend.novels.getInvitationDetails(inviteToken);
          setPendingInvite({
            token: inviteToken,
            novelTitle: res.novelTitle,
            role: res.role,
            ownerId: res.ownerId,
          });
          setDialogOpen(true);
        } catch (err) {
          console.error('Failed to get invite details for token:', err);
        } finally {
          setIsProcessing(false);
        }
        return;
      }

      if (userData?.pendingInvitations && userData.pendingInvitations.length > 0) {
        const firstInvite = userData.pendingInvitations[0];
        setPendingInvite({
          token: firstInvite.token,
          novelTitle: firstInvite.novelTitle,
          role: firstInvite.role,
          ownerId: firstInvite.ownerId,
        });
        setDialogOpen(true);
      }
    };

    checkInvites();
  }, [user, backend, inviteToken, userData?.pendingInvitations]);

  const handleAcceptInvite = async (token: string) => {
    if (!backend) return;
    try {
      setIsProcessing(true);
      await backend.novels.acceptInvitation(token);
      setDialogOpen(false);
      setPendingInvite(null);
      // Hard reload to update selected novel context properly
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to accept invitation:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeclineInvite = async (token: string) => {
    if (!backend) return;
    try {
      setIsProcessing(true);
      await backend.novels.declineInvitation(token);
      setDialogOpen(false);
      setPendingInvite(null);

      // Clean up the URL parameter without page reload
      const url = new URL(window.location.href);
      url.searchParams.delete('inviteToken');
      window.history.replaceState({}, '', url.pathname + url.search);
    } catch (err) {
      console.error('Failed to decline invitation:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    pendingInvite,
    dialogOpen,
    setDialogOpen,
    isProcessing,
    handleAcceptInvite,
    handleDeclineInvite,
  };
}
